import type { DataOption } from '@/common.types'
import type { FieldSchemaModel } from '@/shared/generated'

import React from 'react'
import { CheckIcon, XIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/shared/components/ui/command'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import { cn } from '@/shared/lib/utils'

import { BasicBadgeField } from './BasicBadgeField'

export function BasicSelectEditorField(props: {
	value: string | null
	onChange: (value: string | null) => void
	choices: Record<string, DataOption>
	field: FieldSchemaModel
	disabled?: boolean
	disabledReason?: string
	isDirty: boolean
}) {
	const { t } = useTranslation()

	const [open, setOpen] = React.useState(false)
	if (props.disabled) {
		return (
			<div
				onClick={() =>
					toast.warning(
						props.disabledReason ?? 'You are not allowed to edit this field'
					)
				}
			>
				<BasicBadgeField
					className='cursor-pointer'
					value={props.value}
					choices={props.choices}
				/>
			</div>
		)
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<div className='group relative flex'>
					<BasicBadgeField
						className={cn('cursor-pointer', { 'text-primary': props.isDirty })}
						value={props.value}
						choices={props.choices}
					/>
					{props.value && (
						<Button
							size='icon-xs'
							variant='ghost'
							className='hover:bg-transarent hidden group-hover:inline-flex dark:hover:bg-transparent'
							onClick={(e) => {
								e.stopPropagation()
								props.onChange(null)
							}}
						>
							<XIcon />
						</Button>
					)}
				</div>
			</PopoverTrigger>
			<PopoverContent className='p-0' side='bottom' align='end'>
				<Command>
					<CommandInput
						placeholder={`Serach ${t(props.field.name, { defaultValue: props.field.name }).toLocaleLowerCase()} ...`}
						className='h-9'
					/>
					<CommandList>
						<CommandEmpty>
							No {t(props.field.name, { defaultValue: props.field.name })}{' '}
							found.
						</CommandEmpty>
						<CommandGroup>
							{Object.values(props.choices).map((choice) => (
								<CommandItem
									key={choice.value as string}
									value={choice.value as string}
									keywords={[choice.label]}
									onSelect={(currentValue) => {
										props.onChange(currentValue)
										setOpen(false)
									}}
								>
									{choice.label}
									<CheckIcon
										className={cn(
											'ml-auto',
											props.value === choice.value ? 'opacity-100' : 'opacity-0'
										)}
									/>
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	)
}
